/**
 * Turning data into the words on screen.
 *
 * Pure, and tested, because the copy carries meaning: "date pulled" and
 * "date narrowed" describe opposite feelings and must not be generated by
 * the same template.
 */

import { rangeStart } from '../../poller/dates.js';
import type { EventType, SlateDate } from '../../poller/types.js';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Shows exactly as much as is known and no more. A year-precision date reads
 * "2027", never "1 Jan 2027" — the whole point of carrying precision is that
 * the interface stops pretending.
 */
export function formatDate(date: SlateDate | null): string {
  if (!date) return 'no date';
  const [y, m, d] = date.value.split('-');
  switch (date.precision) {
    case 'year':
      return y ?? date.value;
    case 'month':
      return `${MONTHS[Number(m) - 1] ?? m} ${y}`;
    case 'day':
      return `${Number(d)} ${MONTHS[Number(m) - 1] ?? m} ${y}`;
  }
}

/** "18 Dec" — for histories, where the year is usually obvious from context. */
export function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

export function formatSince(iso: string | null, now: Date): string {
  if (!iso) return 'your first visit';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'your last visit';

  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return `${formatShortDate(iso)}`;
}

/** Whole months between two dates, for the spine's dead-air segments. */
export function monthsBetween(a: SlateDate, b: SlateDate): number {
  const start = rangeStart(a);
  const end = rangeStart(b);
  const months =
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (end.getUTCMonth() - start.getUTCMonth());
  return Math.max(0, months);
}

const SLOT_WORDS: Record<string, string> = {
  primary: 'Release',
  digital: 'Digital release',
  physical: 'Physical release',
  streaming: 'Streaming',
  ebook: 'Ebook',
  audiobook: 'Audiobook',
  paperback: 'Paperback',
};

export function slotWord(slot: unknown): string {
  return SLOT_WORDS[String(slot)] ?? 'Release';
}

export interface Headline {
  label: string;
  detail: string;
}

/**
 * The sentence a change gets in the feed.
 *
 * `date_refined` deliberately reads as detail arriving rather than a schedule
 * slipping — it is the same information getting sharper, and dressing it up
 * as a change would cry wolf every time a publisher pins a month down.
 */
export function headlineFor(type: EventType, payload: Record<string, unknown>): Headline {
  const from = payload.from as SlateDate | undefined;
  const to = payload.to as SlateDate | undefined;
  const on = payload.on as SlateDate | undefined;
  const slot = payload.slot;

  switch (type) {
    case 'date_announced':
      return { label: 'Date announced', detail: formatDate(to ?? null) };
    case 'date_changed': {
      const later = from && to && rangeStart(to) > rangeStart(from);
      return {
        label: later ? 'Pushed back' : 'Brought forward',
        detail: `${formatDate(from ?? null)} → ${formatDate(to ?? null)}`,
      };
    }
    case 'date_refined':
      return { label: 'Date narrowed', detail: formatDate(to ?? null) };
    case 'date_removed':
      return { label: 'Date pulled', detail: `was ${formatDate(from ?? null)}` };
    case 'released':
      return { label: `${slotWord(slot)} is out`, detail: formatDate(on ?? null) };
    case 'format_added':
      return { label: `${slotWord(slot)} coming`, detail: formatDate(on ?? null) };
    case 'work_added': {
      const work = payload.work as { title?: string; releaseDate?: SlateDate | null } | undefined;
      return {
        label: 'New title listed',
        detail: work?.releaseDate ? formatDate(work.releaseDate) : 'no date yet',
      };
    }
    case 'series_completed':
      return { label: 'Series finished', detail: 'nothing more expected' };
  }
}

/** The title a change card shows — usually the item, but a work has its own. */
export function headlineTitle(
  type: EventType,
  payload: Record<string, unknown>,
  itemTitle: string,
): string {
  if (type === 'work_added') {
    const work = payload.work as { title?: string } | undefined;
    if (work?.title) return work.title;
  }
  return itemTitle;
}

export function formatGap(months: number): string {
  if (months >= 24) return `${Math.round(months / 12)} years quiet`;
  if (months >= 12) {
    const years = Math.floor(months / 12);
    const rest = months % 12;
    if (rest === 0) return years === 1 ? 'a year quiet' : `${years} years quiet`;
    return `${years}y ${rest}m quiet`;
  }
  return `${months} months quiet`;
}

/**
 * "10h 23m" — an audiobook's length.
 *
 * Hours and minutes rather than a bare minute count: nobody thinks of a book
 * as 623 minutes long. Seconds are dropped, because an audiobook's length is
 * never quoted to the second and the precision would read as false.
 */
export function formatRuntime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds / 60);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

/** The same, from minutes — which is how film and television quote it. */
export function formatMinutes(minutes: number): string {
  return formatRuntime(minutes * 60);
}

/**
 * "Book 3", "Book 2.5" — a position in a series.
 *
 * Positions are float8 upstream, and half numbers are real: 2.5 is a novella
 * that sits between two novels, and rounding it to 3 would claim it is the
 * third book.
 */
export function formatPosition(position: number | undefined, medium: 'book' | 'film' | 'tv'): string | null {
  if (position == null || !Number.isFinite(position)) return null;
  const noun = medium === 'book' ? 'Book' : 'Part';
  const shown = Number.isInteger(position) ? String(position) : String(position);
  return `${noun} ${shown}`;
}

/** A stored detail date: an ISO day, or a bare year when that is all there is. */
export function formatDetailDate(value: string | undefined): string {
  if (!value) return 'no date';
  if (/^\d{4}$/.test(value)) return value;
  const [y, m, d] = value.split('-');
  if (!m) return y ?? value;
  if (!d) return `${MONTHS[Number(m) - 1] ?? m} ${y}`;
  return `${Number(d)} ${MONTHS[Number(m) - 1] ?? m} ${y}`;
}
